import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Button } from '~/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/components/ui/card'
import { FormInput, FormPassword } from '~/components/ui/form-items/input'
import { useMutation } from '~/hooks/use-mutation'
import { loginSchema, type LoginFormData } from '~/schemas/login'
import { useAuthStore } from '~/stores/auth'
import { MutationKey } from '~/types/mutation-key'

export function LoginPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const setTokens = useAuthStore((state) => state.setTokens)

  const { control, handleSubmit } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  })

  const { mutate: login, isPending } = useMutation<
    { user: unknown; tokens: { accessToken: string; refreshToken: string } },
    LoginFormData
  >({
    mutationKey: MutationKey.LOGIN,
    method: 'POST',
    withToken: false,
    options: {
      onSuccess: (data) => {
        setTokens(data.tokens.accessToken, data.tokens.refreshToken)
        navigate('/')
      },
    },
  })

  const onSubmit = (data: LoginFormData) => {
    login(data)
  }

  return (
    <div className="flex min-h-svh items-center justify-center bg-background p-4" id="login-form-container">
      <Card className="w-full max-w-sm">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl" id="login-title">
            {t('login.title')}
          </CardTitle>
          <CardDescription id="login-subtitle">{t('login.subtitle')}</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <FormInput control={control} name="email" label={t('login.email')} placeholder="admin@herodx.jp" required />
            <FormPassword
              control={control}
              name="password"
              label={t('login.password')}
              placeholder="********"
              required
            />
            <Button type="submit" className="w-full" loading={isPending} id="login-button">
              {t('login.signIn')}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
